import React, { useCallback, useState } from 'react'
import { Link } from 'react-router-dom'
import { ExpandMoreIcon, BusinessContactModal } from '../../lib/components'
import { InvestmentCardV2, type InvestmentCardV2Props } from './investment-card-v2'
import type { ComingSoonPublicItem } from '../../services/coming-soon-service'
import { ComingSoonBand } from '../coming-soon-band/coming-soon-band'
import { OpportunitySpotlight } from '../opportunity-spotlight/opportunity-spotlight'
import './home-v2.scss'

const HOW_IT_WORKS_S3_BASE = 'https://credibila-assets.s3.ap-south-1.amazonaws.com/images-v2'
const HOW_IT_WORKS_STEP_IMAGES = [
  `${HOW_IT_WORKS_S3_BASE}/hiw-1.svg`,
  `${HOW_IT_WORKS_S3_BASE}/hiw-2.svg`,
  `${HOW_IT_WORKS_S3_BASE}/hiw-3.svg`,
]
const HOW_IT_WORKS_ICONS = [
  '/assets/hiw-icon1.svg',
  '/assets/hiw-icon2.svg',
  '/assets/hiw-icon3.svg',
]

const HOW_IT_WORKS_STEPS = [
  {
    step: '01',
    suffix: 'st step',
    title: 'Sign Up',
    description: 'Create a free account & complete your KYC',
  },
  {
    step: '02',
    suffix: 'nd step',
    title: 'Evaluate & Invest',
    description:
      'Go through available investments & choose the one that best fits into your existing portfolio. Transfer funds & request allocation through Credibila once KYC is approved',
  },
  {
    step: '03',
    suffix: 'rd step',
    title: 'Track & Review',
    description:
      "Have access to periodic reports to view your portfolio's composition & performance",
  },
]

const FAQ_ITEMS = [
  {
    question: 'Why do I invest through Credibila',
    answer: (
      <>
        Credibila is a Digital Platform regulated by Financial Services Commission, Mauritius. Our
        goal is to help clients access alternative investment opportunities in private markets,
        venture debt & real estate. Signing up on Credibila allows you to look beyond traditional
        investing asset classes & evaluate alternate investment opportunities.
      </>
    ),
  },
  {
    question: 'Who is eligible to invest through Credibila?',
    answer: (
      <>
        An individual over 18 or an institution is eligible to invest subject to the KYC being
        verified.
      </>
    ),
  },
  {
    question: 'How do I begin Investing through Credibila?',
    answer: (
      <>
        Begin by signing up & getting your KYC verified. Evaluate the opportunities & cherry pick
        basis your financial goals.
      </>
    ),
  },
  {
    question: 'Is there a minimum commitment to invest?',
    answer: (
      <>
        Minimum investment amounts vary by opportunity. You can start building your portfolio from
        US $1,000.
      </>
    ),
  },
  {
    question: 'Are there any fees?',
    answer: (
      <>
        Fee structure is disclosed per opportunity. Check the opportunity details for applicable
        fees.
      </>
    ),
  },
  {
    question: 'Is my confidential information safe with Credibila?',
    answer: (
      <>
        Yes. Credibila follows strict data protection and security practices. Your information is
        handled in line with our Privacy Policy and regulatory requirements.
      </>
    ),
  },
  {
    question: 'Have More Questions',
    answer: (
      <>
        Visit our full FAQ or reach out to us for any other queries.{' '}
        <Link to='/faq' className='home-v2__faq-view-details'>
          View details
        </Link>
      </>
    ),
  },
]

export interface HomeV2PresenterProps {
  highlightedCard?: Omit<InvestmentCardV2Props, 'onCtaClick'>
  opportunityCards: Omit<InvestmentCardV2Props, 'onCtaClick'>[]
  onOpportunityCtaClick: (projectSlug: string, id: number) => void
  opportunityLoading: boolean
  comingSoonItems: ComingSoonPublicItem[]
  comingSoonLoading: boolean
}

export const HomeV2Presenter: React.FC<HomeV2PresenterProps> = ({
  highlightedCard,
  opportunityCards,
  onOpportunityCtaClick,
  opportunityLoading,
  comingSoonItems,
  comingSoonLoading,
}) => {
  const scrollToHowItWorks = useCallback(() => {
    const el = document.querySelector('.home-v2__how-it-works')
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [])

  const [expandedFaqIndex, setExpandedFaqIndex] = useState<number | null>(null)
  const [showGetInTouchModal, setShowGetInTouchModal] = useState(false)
  const [ctaEmail, setCtaEmail] = useState('')

  return (
    <div className='home-v2' data-testid='home-v2-page'>
      <section className='home-v2__hero'>
        <video
          className='home-v2__hero-video'
          autoPlay
          muted
          loop
          playsInline
          preload='metadata'
          poster='https://credibila-assets.s3.ap-south-1.amazonaws.com/images-v2/hero.jpg'
          aria-hidden='true'
        >
          <source
            src='https://credibila-assets.s3.ap-south-1.amazonaws.com/videos/home-hero-video.mp4'
            type='video/mp4'
          />
        </video>
        <div className='home-v2__hero-inner'>
          <h1 className='home-v2__hero-title'>Invest in What Moves the World</h1>
          <p className='home-v2__hero-subtitle'>
            Curated Access to Alternatives: Private Equity, Venture Debt, Real Estate, and beyond
          </p>
          <div className='home-v2__hero-actions'>
            <Link
              to='/opportunities'
              className='home-v2__btn-primary'
              data-testid='home-v2-explore'
            >
              Explore
            </Link>
            <button
              type='button'
              className='home-v2__btn-secondary'
              onClick={scrollToHowItWorks}
              data-testid='home-v2-how-it-works-btn'
            >
              See How It Works →
            </button>
          </div>
        </div>
      </section>

      <section className='home-v2__marketplace'>
        <div className='home-v2__marketplace-inner'>
          <div className='home-v2__marketplace-header'>
            <div>
              <p className='home-v2__marketplace-eyebrow'>INVESTMENT MARKETPLACE</p>
            </div>
            <Link to='/opportunities' className='home-v2__marketplace-view-all'>
              View all →
            </Link>
          </div>
          {opportunityLoading ? (
            <p className='home-v2__loading' data-testid='home-v2-loading'>
              Loading…
            </p>
          ) : opportunityCards.length === 0 ? (
            <p className='home-v2__empty' data-testid='home-v2-empty'>
              No results found.
            </p>
          ) : (
            <div className='home-v2__cards-wrap'>
              <div className='home-v2__cards-grid' data-testid='home-v2-opportunity-cards'>
                {opportunityCards.map((card) => (
                  <InvestmentCardV2
                    key={card.id}
                    {...card}
                    onCtaClick={() => onOpportunityCtaClick(card.projectSlug, card.id)}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {highlightedCard ? (
        <OpportunitySpotlight
          title={highlightedCard.title}
          description={highlightedCard.description}
          category={highlightedCard.category}
          minInvestment={highlightedCard.minInvestment}
          onInvestClick={() =>
            onOpportunityCtaClick(highlightedCard.projectSlug, highlightedCard.id)
          }
          testIdPrefix='home-v2'
        />
      ) : comingSoonItems.length > 0 ? (
        <ComingSoonBand
          items={comingSoonItems}
          loading={comingSoonLoading}
          testIdPrefix='home-v2'
        />
      ) : null}

      <section className='home-v2__why-invest'>
        <div className='home-v2__why-invest-inner'>
          <h2 className='home-v2__why-invest-title'>Why Invest with Credibila?</h2>
          <p className='home-v2__why-invest-subtitle'>
            Designed for serious clients, built by experts.
          </p>
          <div className='home-v2__features-grid'>
            <div className='home-v2__feature-card' data-testid='home-v2-feature-multi-asset'>
              <img
                src='/assets/why-invest-icon1.svg'
                alt=''
                className='home-v2__feature-icon-img'
                width={50}
                height={50}
              />
              <h3 className='home-v2__feature-title'>Multi-Asset Access</h3>
              <p className='home-v2__feature-desc'>
                Diversify across private equity, venture debt, trade finance, and more.
              </p>
            </div>
            <div className='home-v2__feature-card' data-testid='home-v2-feature-diligence'>
              <img
                src='/assets/why-invest-icon2.svg'
                alt=''
                className='home-v2__feature-icon-img'
                width={50}
                height={50}
              />
              <h3 className='home-v2__feature-title'>Institutional Due Diligence</h3>
              <p className='home-v2__feature-desc'>
                Every deal undergoes multi-layer evaluation by Advisory Partner.
              </p>
            </div>
            <div className='home-v2__feature-card' data-testid='home-v2-feature-seamless'>
              <img
                src='/assets/why-invest-icon3.svg'
                alt=''
                className='home-v2__feature-icon-img'
                width={50}
                height={50}
              />
              <h3 className='home-v2__feature-title'>Seamless Investing</h3>
              <p className='home-v2__feature-desc'>
                Invest, track, and manage payouts securely in one place.
              </p>
            </div>
            <div className='home-v2__feature-card' data-testid='home-v2-feature-low-minimums'>
              <img
                src='/assets/why-invest-icon4.svg'
                alt=''
                className='home-v2__feature-icon-img'
                width={50}
                height={50}
              />
              <h3 className='home-v2__feature-title'>Low Minimums, Global Reach</h3>
              <p className='home-v2__feature-desc'>
                Start with US $1,000 and co-invest alongside global institutions.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className='home-v2__how-it-works' aria-label='How it works'>
        <div className='home-v2__how-it-works-inner'>
          <h2 className='home-v2__how-it-works-title'>How it Works?</h2>
          <div className='home-v2__steps-wrap'>
            <div className='home-v2__steps-dotted-line' aria-hidden />
            <div className='home-v2__steps'>
              {HOW_IT_WORKS_STEPS.map(({ step, title, description }, idx) => (
                <div key={step} className='home-v2__step'>
                  <div className='home-v2__step-image-wrap'>
                    <img
                      src={HOW_IT_WORKS_ICONS[idx]}
                      alt=''
                      className='home-v2__step-icon'
                      aria-hidden
                    />
                    <img
                      src={HOW_IT_WORKS_STEP_IMAGES[idx]}
                      alt=''
                      className='home-v2__step-image'
                      data-testid={`home-v2-step-image-${step}`}
                    />
                  </div>
                  <h3 className='home-v2__step-title'>{title}</h3>
                  <p className='home-v2__step-desc'>{description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className='home-v2__faq'>
        <div className='home-v2__faq-inner'>
          <h2 className='home-v2__faq-title'>Frequently Asked Questions</h2>
          <ul className='home-v2__faq-list'>
            {FAQ_ITEMS.map((item, index) => (
              <li key={item.question} className='home-v2__faq-item'>
                <button
                  type='button'
                  className='home-v2__faq-question'
                  onClick={() => setExpandedFaqIndex(expandedFaqIndex === index ? null : index)}
                  aria-expanded={expandedFaqIndex === index}
                >
                  <span className='home-v2__faq-question-text'>{item.question}</span>
                  <span
                    className={`home-v2__faq-chevron ${
                      expandedFaqIndex === index ? 'home-v2__faq-chevron--expanded' : ''
                    }`}
                    aria-hidden
                  >
                    <ExpandMoreIcon width='12' height='9' />
                  </span>
                </button>
                {expandedFaqIndex === index && (
                  <div className='home-v2__faq-answer'>{item.answer}</div>
                )}
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className='home-v2__cta'>
        <div className='home-v2__cta-inner'>
          <div className='home-v2__cta-content'>
            <h2 className='home-v2__cta-title'>Looking to raise capital?</h2>
            <p className='home-v2__cta-desc'>
              Partner with Credibila to showcase your institutional-grade projects to our network of
              <br />
              verified clients.
            </p>
          </div>
          <div className='home-v2__cta-email-row'>
            <div className='home-v2__cta-email-wrap'>
              <span className='home-v2__cta-email-icon' aria-hidden>
                <img src='/assets/mail-line.svg' alt='' width={24} height={24} />
              </span>
              <input
                type='email'
                className='home-v2__cta-email-input'
                placeholder='Enter your company email address'
                value={ctaEmail}
                onChange={(e) => setCtaEmail(e.target.value)}
                aria-label='Email address'
              />
            </div>
            <button
              type='button'
              className='home-v2__cta-btn'
              onClick={() => setShowGetInTouchModal(true)}
            >
              Contact Us
            </button>
          </div>
        </div>
      </section>
      {showGetInTouchModal && (
        <BusinessContactModal
          active={showGetInTouchModal}
          handleOnClose={() => setShowGetInTouchModal(false)}
          initialEmail={ctaEmail}
        />
      )}
    </div>
  )
}
